import { useCallback, useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Copy, Download, Eye, FileSpreadsheet, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Tabs } from '@/components/ui/Tabs'
import { NumberInput } from '@/components/ui/NumberInput'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { FixedTable, RowIconButton, SectionHeader, type FixedColumn } from '@/components/ui/FixedTable'
import { Pill, type PillTone } from '@/components/ui/StatusPill'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'
import { exportToXlsx, type ExportColumn } from '@/lib/exportXlsx'
import { useAuthority } from '@/hooks/useAuthority'
import {
  ProposalPricingService as Service,
  type PricingTableDetail,
  type PricingTableSummary,
} from '@/services/modules/ProposalPricingService'
import { ProposalCatalogNav } from './ProposalCatalogNav'
import { PricingImportModal } from './ProposalPricingImport'

const STATUS_TONE: Record<string, PillTone> = {
  ATIVA: 'green',
  RASCUNHO: 'amber',
  ARQUIVADA: 'gray',
}

const EXPORT_COLUMNS: ExportColumn<PricingTableSummary>[] = [
  { header: 'Versão',      accessor: t => `v${t.version}` },
  { header: 'Tabela',      accessor: t => t.name },
  { header: 'Observações', accessor: t => t.notes ?? '' },
  { header: 'Status',      accessor: t => t.status.toLowerCase() },
  { header: 'Preços',      accessor: t => t.counts.prices },
  { header: 'Pesos',       accessor: t => t.counts.weights },
  { header: 'Faixas',      accessor: t => t.counts.bands },
  { header: 'Ativada em',  accessor: t => t.activatedAt ? new Date(t.activatedAt).toLocaleString('pt-BR') : '' },
  { header: 'Ativada por', accessor: t => t.activatedBy ?? '' },
]

const pct = (v: number) => `${(v * 100).toFixed(3).replace('.', ',')}%`
const money = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Tabela de precificação da proposta.
 *
 * Uma versão só fica ATIVA por vez e ela não é editável: para reajustar,
 * duplica-se num rascunho, ajusta-se e ativa. Proposta já gerada não muda —
 * ela congela os números no snapshot.
 */
export function SettingsProposalPricingPage() {
  const { can } = useAuthority()
  const canDuplicate = can('settings.proposal_pricing.create')
  const canActivate = can('settings.proposal_pricing.activate')
  const canEdit = can('settings.proposal_pricing.edit')
  const canDelete = can('settings.proposal_pricing.delete')
  const [tables, setTables] = useState<PricingTableSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [openId, setOpenId] = useState<string | null>(null)
  const [duplicating, setDuplicating] = useState<PricingTableSummary | null>(null)
  const [removing, setRemoving] = useState<PricingTableSummary | null>(null)
  const [busy, setBusy] = useState(false)
  const [importing, setImporting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setTables(await Service.list())
    } catch (e) {
      toast.error({ title: 'Não foi possível carregar', description: extractApiError(e).message })
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  async function activate(row: PricingTableSummary) {
    setBusy(true)
    try {
      await Service.activate(row.id)
      toast.success({ title: 'Versão ativada', description: `A versão ${row.version} está no ar.` })
      await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível ativar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function duplicate(name: string) {
    if (!duplicating) return
    setBusy(true)
    try {
      const copy = await Service.duplicate(duplicating.id, name)
      toast.success({ title: 'Rascunho criado', description: `Versão ${copy.version}.` })
      setDuplicating(null)
      await load()
      setOpenId(copy.id)
    } catch (e) {
      toast.error({ title: 'Não foi possível duplicar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function remove() {
    if (!removing) return
    setBusy(true)
    try {
      await Service.destroy(removing.id)
      toast.success({ title: 'Versão removida', description: removing.name })
      setRemoving(null); await load()
    } catch (e) {
      toast.error({ title: 'Não foi possível remover', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const STATUS_LABEL: Record<string, string> = { ATIVA: 'Ativa', RASCUNHO: 'Rascunho', ARQUIVADA: 'Arquivada' }
  const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null)
  const active = tables.find(t => t.status === 'ATIVA') ?? null

  const columns: FixedColumn<PricingTableSummary>[] = [
    { key: 'version', header: 'Versão', width: 96, render: t => <span className="font-semibold tabular-nums text-gray-900">v{t.version}</span> },
    { key: 'name', header: 'Nome', render: t => (
      <div className="space-y-0.5 py-1">
        <p className="font-medium text-gray-900">{t.name}</p>
        <p className="text-xs text-gray-500">{t.notes || 'Sem observações'}</p>
      </div>
    ) },
    { key: 'status', header: 'Status', width: 128, render: t => <Pill tone={STATUS_TONE[t.status] ?? 'gray'}>{STATUS_LABEL[t.status] ?? t.status}</Pill> },
    { key: 'counts', header: 'Conteúdo', width: 260, render: t => (
      <span className="tabular-nums text-gray-600">{t.counts.prices} preços · {t.counts.weights} pesos · {t.counts.bands} faixas</span>
    ) },
    { key: 'activated', header: 'Ativada em', width: 220, render: t => (
      t.activatedAt
        ? <span className="text-gray-600">{fmtDate(t.activatedAt)}{t.activatedBy ? ` · ${t.activatedBy}` : ''}</span>
        : <span className="text-gray-400">—</span>
    ) },
    { key: 'actions', header: <span className="sr-only">Ações</span>, width: 176, align: 'right', render: t => (
      <div className="flex justify-end">
        <RowIconButton label={t.status === 'RASCUNHO' && canEdit ? `Editar valores da v${t.version}` : `Ver valores da v${t.version}`} onClick={() => setOpenId(t.id)}>
          {t.status === 'RASCUNHO' && canEdit ? <Pencil size={15} /> : <Eye size={15} />}
        </RowIconButton>
        {canDuplicate && <RowIconButton label={`Duplicar v${t.version} como rascunho`} onClick={() => setDuplicating(t)}><Copy size={15} /></RowIconButton>}
        {t.status !== 'ATIVA' && canActivate && <RowIconButton label={`Ativar v${t.version}`} disabled={busy} onClick={() => activate(t)}><CheckCircle2 size={15} /></RowIconButton>}
        {t.status !== 'ATIVA' && canDelete && <RowIconButton label={`Remover v${t.version}`} danger onClick={() => setRemoving(t)}><Trash2 size={15} /></RowIconButton>}
      </div>
    ) },
  ]

  return (
    <>
      <PageHeader
        title="Catálogo de propostas"
        subtitle="Tabelas de preço: preço cheio, peso das coberturas e faixas de desconto. Uma versão fica no ar por vez."
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Tabelas de preço' }]}
      />
      <ProposalCatalogNav />

      <div className="space-y-4">
        <section aria-labelledby="tabela-no-ar" className="flex flex-wrap items-center gap-6 rounded-xl bg-white px-6 py-5 ring-1 ring-gray-200">
          <div className="min-w-0 flex-[1_1_360px] space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Tabela no ar</p>
            {active ? (
              <>
                <h2 id="tabela-no-ar" className="flex flex-wrap items-center gap-2.5 text-lg font-semibold text-gray-900">
                  v{active.version} · {active.name} <Pill tone="green">Ativa</Pill>
                </h2>
                <p className="text-[13px] text-gray-600">
                  {active.activatedAt ? `Ativada em ${fmtDate(active.activatedAt)}${active.activatedBy ? ` por ${active.activatedBy}` : ''}. ` : ''}
                  Vale para as propostas novas; as já salvas guardam a versão delas.
                </p>
              </>
            ) : (
              <>
                <h2 id="tabela-no-ar" className="text-lg font-semibold text-gray-900">Nenhuma tabela ativa</h2>
                <p className="text-[13px] text-amber-800">Sem tabela ativa, nenhuma linha aparece nas propostas. Importe uma planilha ou ative uma versão abaixo.</p>
              </>
            )}
          </div>
          {active && (
            <dl className="grid flex-[0_1_380px] grid-cols-3 gap-6">
              <div><dt className="text-xs text-gray-500">Preços cheios</dt><dd className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{active.counts.prices}</dd></div>
              <div><dt className="text-xs text-gray-500">Pesos</dt><dd className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{active.counts.weights}</dd></div>
              <div><dt className="text-xs text-gray-500">Faixas de desconto</dt><dd className="mt-0.5 text-xl font-semibold tabular-nums text-gray-900">{active.counts.bands}</dd></div>
            </dl>
          )}
          <div className="flex flex-wrap gap-2">
            {active && <Button variant="secondary" iconLeft={<Eye size={15} />} onClick={() => setOpenId(active.id)}>Ver valores</Button>}
            {canDuplicate && <Button onClick={() => setImporting(true)} iconLeft={<FileSpreadsheet size={15} />}>Importar planilha</Button>}
          </div>
        </section>

        <section className="overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
          <SectionHeader title="Todas as versões"
            description="Uma versão fica no ar por vez. Rascunhos se ajustam e ativam; versões usadas por propostas não podem ser removidas."
            actions={tables.length > 0 ? (
              <Button variant="secondary" iconLeft={<Download size={15} />} onClick={() => exportToXlsx('tabelas-precificacao', tables, EXPORT_COLUMNS)}>Exportar</Button>
            ) : undefined} />
          <div className="border-t border-gray-200">
            <FixedTable columns={columns} rows={tables} getRowKey={t => t.id} loading={loading}
              rowClassName={t => (t.status === 'ATIVA' ? 'bg-brand-50/40' : undefined)}
              emptyMessage="Nenhuma versão ainda. Use “Importar planilha” para criar a primeira." minWidth={1000} />
          </div>
        </section>
      </div>

      <PricingImportModal open={importing} canActivate={canActivate}
        onClose={() => setImporting(false)}
        onDone={async id => { setImporting(false); await load(); setOpenId(id) }} />

      <VersionEditor id={openId} canEdit={canEdit} onClose={() => setOpenId(null)} onSaved={load} />

      <DuplicateModal
        table={duplicating}
        busy={busy}
        onClose={() => setDuplicating(null)}
        onConfirm={duplicate}
      />

      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        title="Remover versão?"
        description={`Remover "${removing?.name}"? Versões usadas por propostas são bloqueadas.`}
        confirmLabel="Remover"
        variant="danger"
      />
    </>
  )
}

function DuplicateModal({ table, busy, onClose, onConfirm }: {
  table: PricingTableSummary | null
  busy: boolean
  onClose: () => void
  onConfirm: (name: string) => void
}) {
  const [name, setName] = useState('')
  useEffect(() => { if (table) setName(`${table.name} (reajuste)`) }, [table])

  return (
    <Modal open={!!table} onClose={onClose} title="Duplicar tabela de precificação" size="md"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancelar</Button>
        <Button loading={busy} disabled={!name.trim()} onClick={() => onConfirm(name.trim())}>Duplicar</Button>
      </>}>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          A cópia nasce como <strong>rascunho</strong> com os mesmos preços, pesos e faixas. Ajuste o que
          precisar e ative quando estiver pronta — o que está no ar não muda até lá.
        </p>
        <Input label="Nome da nova versão" required value={name} onChange={e => setName(e.target.value)} />
      </div>
    </Modal>
  )
}

type EditorTab = 'prices' | 'weights' | 'bands'

function VersionEditor({ id, canEdit, onClose, onSaved }: {
  id: string | null
  /** PUT /settings/commercial/proposal-pricing/{id}/content exige `settings.proposal_pricing.edit`. */
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [detail, setDetail] = useState<PricingTableDetail | null>(null)
  const [tab, setTab] = useState<EditorTab>('prices')
  const [prices, setPrices] = useState<Record<string, number>>({})
  const [weights, setWeights] = useState<Record<string, number>>({})
  const [bands, setBands] = useState<Record<string, number>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!id) { setDetail(null); return }
    Service.show(id).then(d => {
      setDetail(d)
      setPrices(Object.fromEntries(d.prices.map(p => [p.tierId, p.fullPrice])))
      setWeights(Object.fromEntries(d.weights.map(w => [`${w.tierId}|${w.itemId}`, w.weight])))
      setBands(Object.fromEntries(d.bands.map(b => [b.id, b.percent])))
      setTab('prices')
    }).catch(e => toast.error({ title: 'Não foi possível abrir', description: extractApiError(e).message }))
  }, [id])

  /** Soma dos pesos por tipologia — precisa fechar 1,0 para a versão ativar. */
  const sums = useMemo(() => {
    const out: Record<string, number> = {}
    for (const w of detail?.weights ?? []) {
      const v = weights[`${w.tierId}|${w.itemId}`] ?? 0
      out[w.tierId] = (out[w.tierId] ?? 0) + v
    }
    return out
  }, [detail, weights])

  async function save() {
    if (!detail) return
    setSaving(true)
    try {
      const problems = await Service.saveContent(detail.id, {
        prices: detail.prices.map(p => ({ tierId: p.tierId, fullPrice: prices[p.tierId] ?? 0 })),
        weights: detail.weights.map(w => ({
          tierId: w.tierId, itemId: w.itemId, weight: weights[`${w.tierId}|${w.itemId}`] ?? 0,
        })),
        bands: detail.bands.map(b => ({
          curveId: b.curveId, minLives: b.minLives, maxLives: b.maxLives, percent: bands[b.id] ?? 0,
        })),
      })
      setDetail({ ...detail, problems })
      if (problems.length === 0) {
        toast.success({ title: 'Valores salvos', description: 'A versão está pronta para ativar.' })
      } else {
        toast.error({ title: 'Salvo com pendências', description: problems[0] })
      }
      onSaved()
    } catch (e) {
      toast.error({ title: 'Não foi possível salvar', description: extractApiError(e).message })
    } finally { setSaving(false) }
  }

  // Sem permissão de edição, o rascunho abre só para leitura.
  const editable = (detail?.editable ?? false) && canEdit
  const byTier = useMemo(() => {
    const map = new Map<string, { tierName: string; category: string; rows: PricingTableDetail['weights'] }>()
    for (const w of detail?.weights ?? []) {
      if (!map.has(w.tierId)) map.set(w.tierId, { tierName: w.tierName, category: w.category, rows: [] })
      map.get(w.tierId)!.rows.push(w)
    }
    return [...map.entries()]
  }, [detail])

  const byCurve = useMemo(() => {
    const map = new Map<string, { name: string; rows: PricingTableDetail['bands'] }>()
    for (const b of detail?.bands ?? []) {
      if (!map.has(b.curveId)) map.set(b.curveId, { name: b.curveName, rows: [] })
      map.get(b.curveId)!.rows.push(b)
    }
    return [...map.entries()]
  }, [detail])

  return (
    <Modal open={!!id} onClose={onClose} size="xl"
      title={detail ? `Versão ${detail.version} — ${detail.name}` : 'Carregando…'}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Fechar</Button>
        {editable && <Button loading={saving} onClick={save}>Salvar valores</Button>}
      </>}>
      {!detail ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : (
        <div className="space-y-4">
          {!editable && (
            <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
              {detail.editable
                ? 'Você não tem permissão para editar os valores desta versão.'
                : <>Esta versão está {detail.status.toLowerCase()} e não pode ser editada. Duplique-a para reajustar.</>}
            </div>
          )}
          {detail.problems.length > 0 && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-800 ring-1 ring-red-200">
              <p className="mb-1 font-medium">Pendências que impedem a ativação:</p>
              <ul className="list-disc pl-4">
                {detail.problems.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}

          <Tabs
            items={[
              { id: 'prices', label: 'Preços cheios' },
              { id: 'weights', label: 'Pesos das coberturas' },
              { id: 'bands', label: 'Curvas de desconto' },
            ]}
            active={tab}
            onChange={(t) => setTab(t as EditorTab)}
          />

          {tab === 'prices' && (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2">Categoria</th>
                  <th className="py-2">Tipologia</th>
                  <th className="py-2 w-48">Preço cheio</th>
                </tr>
              </thead>
              <tbody>
                {detail.prices.map(p => (
                  <tr key={p.id} className="border-t border-gray-100">
                    <td className="py-2 text-gray-600">{p.category}</td>
                    <td className="py-2 font-medium text-gray-800">{p.tierName}</td>
                    <td className="py-2">
                      {editable ? (
                        <CurrencyInput value={prices[p.tierId] ?? 0}
                          onChange={v => setPrices(s => ({ ...s, [p.tierId]: v ?? 0 }))} />
                      ) : money(p.fullPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === 'weights' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                O peso é a fatia do preço que cada cobertura representa. A soma de cada tipologia
                tem de fechar 1,0 — desmarcar uma cobertura na proposta abate exatamente esse peso.
              </p>
              {byTier.map(([tierId, group]) => {
                const sum = sums[tierId] ?? 0
                const ok = Math.abs(sum - 1) < 0.000001
                return (
                  <div key={tierId} className="rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2">
                      <span className="text-sm font-medium text-gray-800">{group.category} · {group.tierName}</span>
                      <Badge variant={ok ? 'green' : 'red'}>soma {sum.toFixed(6).replace('.', ',')}</Badge>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {group.rows.map(w => (
                          <tr key={w.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-700">{w.itemName}</td>
                            <td className="px-3 py-2 w-40">
                              {editable ? (
                                <NumberInput value={weights[`${w.tierId}|${w.itemId}`] ?? 0} decimals={6} min={0} max={1}
                                  onChange={v => setWeights(s => ({ ...s, [`${w.tierId}|${w.itemId}`]: v ?? 0 }))} />
                              ) : w.weight.toFixed(6).replace('.', ',')}
                            </td>
                            <td className="px-3 py-2 w-24 text-right text-xs text-gray-500">
                              {((weights[`${w.tierId}|${w.itemId}`] ?? 0) * 100).toFixed(2).replace('.', ',')}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              })}
            </div>
          )}

          {tab === 'bands' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">
                Desconto por volume de vidas. A última faixa não tem teto: acima dela o desconto
                congela no último valor válido.
              </p>
              {byCurve.map(([curveId, group]) => (
                <div key={curveId} className="rounded-lg border border-gray-200">
                  <div className="border-b border-gray-100 px-3 py-2 text-sm font-medium text-gray-800">{group.name}</div>
                  <div className="max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white">
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                          <th className="px-3 py-2">Vidas</th>
                          <th className="px-3 py-2 w-44">Desconto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map(b => (
                          <tr key={b.id} className="border-t border-gray-100">
                            <td className="px-3 py-1.5 text-gray-700">
                              {b.maxLives === null ? `${b.minLives} ou mais` : `${b.minLives} a ${b.maxLives}`}
                            </td>
                            <td className="px-3 py-1.5">
                              {editable ? (
                                <NumberInput value={bands[b.id] ?? 0} decimals={6} min={0} max={1}
                                  onChange={v => setBands(s => ({ ...s, [b.id]: v ?? 0 }))} />
                              ) : pct(b.percent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
